// App
import { type FC, type KeyboardEvent, type MouseEvent, type RefObject, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';

// Misc / utils
import * as S from './styles';

interface CommuteButtonDropdownProps {
  ariaLabel: string;
  buttonRef: RefObject<HTMLButtonElement>;
  currentButtonValue: string;
  dropDownItemRefList: Array<RefObject<HTMLLIElement>>;
  dropDownItemClick: (e: MouseEvent<HTMLLIElement>) => void;
  dropDownItemKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
  isButtonDisabled: boolean;
  labelId: string;
  onButtonClick: () => void;
  onButtonKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  options: Array<string>;
  showDropdown: boolean;
}

const CommuteButtonDropdown: FC<CommuteButtonDropdownProps> = ({
  ariaLabel,
  buttonRef,
  currentButtonValue,
  dropDownItemRefList,
  dropDownItemClick,
  dropDownItemKeyDown,
  isButtonDisabled,
  labelId,
  onButtonClick,
  onButtonKeyDown,
  options,
  showDropdown,
}) => {
  const isLoaded = useRef<boolean>(false);
  useEffect(() => {
    /**
     * isLoaded is a component instance variable. Do not set focus on the button until
     * after the component has been interacted with.
     * @see: https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
     */
    if (!isLoaded.current) {
      isLoaded.current = true;
    } else {
      if (!showDropdown && buttonRef.current) {
        buttonRef.current.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentButtonValue]);

  return (
    <FocusLock disabled={!showDropdown}>
      <S.DropdownButton
        aria-label={ariaLabel}
        aria-disabled={isButtonDisabled}
        aria-expanded={showDropdown}
        disabled={isButtonDisabled}
        id={labelId}
        onClick={onButtonClick}
        onKeyDown={onButtonKeyDown}
        ref={buttonRef}
        showCarat
      >
        {currentButtonValue}
      </S.DropdownButton>
      {showDropdown && (
        <S.DropdownList>
          {options.map((option: string, idx: number) => {
            return (
              <S.DropdownListItem
                aria-selected={option === currentButtonValue}
                tabIndex={idx}
                data-option-value={option}
                onKeyDown={dropDownItemKeyDown}
                onClick={dropDownItemClick}
                ref={dropDownItemRefList[idx]}
                role="option"
                key={`dropdown-option-${option}-${idx}`}
              >
                {option}
              </S.DropdownListItem>
            );
          })}
        </S.DropdownList>
      )}
    </FocusLock>
  );
};

export default CommuteButtonDropdown;
